import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const QuickAssign = ({ isOpen, onClose }) => {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    assignTo: '',
    assignRole: '',
    title: '',
    description: '',
    priority: 'normal'
  });

  useEffect(() => {
    if (isOpen) {
      loadUsers();
    }
  }, [isOpen]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await api.getUsers();
      console.log('QuickAssign loaded users:', data);
      // Filter out current user but keep all others
      const filteredUsers = (data || []).filter(u => u.id !== currentUser?.id);
      console.log('QuickAssign filtered users:', filteredUsers);
      setUsers(filteredUsers);
    } catch (error) {
      console.error('Failed to load users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      alert('Please enter a task title');
      return;
    }
    if (!form.assignTo && !form.assignRole) {
      alert('Please select a person or role to assign to');
      return;
    }

    setSaving(true);
    try {
      await api.createTask({
        title: form.title,
        description: form.description,
        assignedTo: form.assignTo || null,
        assignedRole: form.assignRole || null,
        priority: form.priority,
        status: 'pending'
      });
      
      // Reset form
      setForm({
        assignTo: '',
        assignRole: '',
        title: '',
        description: '',
        priority: 'normal'
      });
      
      onClose();
      alert('Task assigned successfully!');
    } catch (error) {
      console.error('Failed to create task:', error);
      alert('Failed to assign task: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  // Group users by role - include all possible roles
  const roleGroups = {
    admin: users.filter(u => u.role === 'admin'),
    office: users.filter(u => u.role === 'office'),
    shop: users.filter(u => u.role === 'shop'),
    detail: users.filter(u => u.role === 'detail'),
    buyer: users.filter(u => u.role === 'buyer'),
    inventory_viewer: users.filter(u => u.role === 'inventory_viewer'),
  };
  
  // Also catch any users with roles not in the standard list
  const knownRoles = ['admin', 'office', 'shop', 'detail', 'buyer', 'inventory_viewer'];
  const otherUsers = users.filter(u => !knownRoles.includes(u.role));
  if (otherUsers.length > 0) {
    roleGroups.other = otherUsers;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-orange-500 text-white rounded-t-xl">
          <div className="flex items-center gap-3">
            <span className="text-2xl"></span>
            <h2 className="text-xl font-bold">Quick Assign</h2>
          </div>
          <button onClick={onClose} className="text-white hover:bg-orange-600 p-2 rounded-lg">
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Assign To */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Assign To Person</label>
            <select
              value={form.assignTo}
              onChange={e => setForm({ ...form, assignTo: e.target.value, assignRole: '' })}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="">-- Select Person --</option>
              {Object.entries(roleGroups).map(([role, roleUsers]) => (
                roleUsers.length > 0 && (
                  <optgroup key={role} label={role.charAt(0).toUpperCase() + role.slice(1)}>
                    {roleUsers.map(user => (
                      <option key={user.id} value={user.id}>{user.name}</option>
                    ))}
                  </optgroup>
                )
              ))}
            </select>
          </div>

          {/* Or Assign To Role */}
          <div className="text-center text-sm text-neutral-500">— OR —</div>
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Assign To Role (anyone in role)</label>
            <select
              value={form.assignRole}
              onChange={e => setForm({ ...form, assignRole: e.target.value, assignTo: '' })}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="">-- Select Role --</option>
              <option value="admin">Admin</option>
              <option value="office">Office</option>
              <option value="shop">Shop / Recon</option>
              <option value="detail">Detail</option>
            </select>
          </div>

          {/* Task Title */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">What do you need? *</label>
            <input
              type="text"
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder="e.g., Check on Stock #1234, Review invoice, etc."
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Details (optional)</label>
            <textarea
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="Add any additional details..."
              rows={3}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Priority</label>
            <div className="flex gap-2">
              {['low', 'normal', 'high', 'urgent'].map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setForm({ ...form, priority: p })}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition ${
                    form.priority === p
                      ? p === 'urgent' ? 'bg-red-500 text-white' :
                        p === 'high' ? 'bg-orange-500 text-white' :
                        p === 'normal' ? 'bg-blue-500 text-white' :
                        'bg-neutral-500 text-white'
                      : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                  }`}
                >
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-neutral-300 rounded-lg text-neutral-700 hover:bg-neutral-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || loading}
              className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
            >
              {saving ? 'Assigning...' : 'Assign Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickAssign;
